import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getFirestore,
  Firestore,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getMessaging, getToken, Messaging } from "firebase/messaging";

import { generateDeviceId, registerServiceWorker } from "./utils";

declare global {
  interface Window {
    firebaseApp?: FirebaseApp;
    firebaseDb?: Firestore;
    firebaseMessaging?: Messaging;
  }
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

class FirebaseService {
  public app: FirebaseApp;
  public db: Firestore;
  public messaging: Messaging;

  private static instance: FirebaseService;

  private constructor() {
    // Check if Firebase has already been initialized and attached to the window
    if (window?.firebaseApp && window.firebaseDb && window.firebaseMessaging) {
      console.log("Reusing existing Firebase instance from window.");
      this.app = window.firebaseApp;
      this.db = window.firebaseDb;
      this.messaging = window.firebaseMessaging;
    } else {
      console.log("Initializing a new Firebase instance.");
      this.app = initializeApp(firebaseConfig);
      this.db = getFirestore(this.app);
      this.messaging = getMessaging(this.app);

      window.firebaseApp = this.app;
      window.firebaseDb = this.db;
      window.firebaseMessaging = this.messaging;
    }

    this.registerWebClient();
  }

  /**
   * Provides a single instance of the FirebaseService.
   * @returns {FirebaseService} The singleton instance.
   */
  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  private async registerWebClient(): Promise<void> {
    try {
      const clientId = Math.random().toString(36).substring(2); // simple uid
      const conversationDocRef = doc(this.db, "conversations", clientId);

      await setDoc(conversationDocRef, {
        platform: "web",
        connectedAt: serverTimestamp(),
        deviceId: generateDeviceId(),
      });
      console.log("Web client registered successfully with ID:", clientId);
    } catch (error) {
      console.error("Failed to register web client:", error);
    }
  }

  /**
   * Generates a Firebase Cloud Messaging (FCM) token for push notifications.
   * @param {number} maxRetries - The maximum number of times to retry token generation.
   * @param {number} retryDelay - The delay in milliseconds between retries.
   * @returns {Promise<string | null>} The FCM token, or null if it fails.
   */
  public async generateTokenFCM(
    maxRetries = 3,
    retryDelay = 1000
  ): Promise<string | null> {
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      console.error("FCM requires HTTPS or localhost for service workers.");
      return null;
    }

    let retries = 0;
    while (retries < maxRetries) {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.warn("Notification permission not granted.");
          return null;
        }

        const registration = await registerServiceWorker();
        if (!registration) {
          throw new Error("Service worker registration failed.");
        }

        // Send config to the service worker
        registration.active?.postMessage({
          type: "FIREBASE_CONFIG",
          config: firebaseConfig,
        });

        const token = await getToken(this.messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (!token) {
          throw new Error("No FCM token received.");
        }

        // Success, return the token
        return token;
      } catch (error) {
        console.error(
          `Attempt ${retries + 1} to generate FCM token failed:`,
          error
        );
        retries++;
        if (retries < maxRetries) {
          await new Promise((res) => setTimeout(res, retryDelay));
        } else {
          console.error("Failed to generate FCM token after all retries.");
          return null;
        }
      }
    }
    return null;
  }
}

// Export a single instance of the service for the entire app to use.
const firebaseService = FirebaseService.getInstance();
export default firebaseService;
